import { useCallback, useEffect, useState } from "react";
import { Download, Loader2, Search, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, downloadFile, formatErr } from "@/lib/api";
import { toast } from "sonner";

const METHOD = { signup_checkbox: "Sign-up checkbox", login_confirmation: "Login confirmation" };
const EMPTY = { q: "", provider: "all", status: "all", date_from: "", date_to: "" };

const clean = (f) => Object.fromEntries(Object.entries(f).filter(([, v]) => v && v !== "all"));

function Filters({ f, setF }) {
  const set = (k) => (v) => setF({ ...f, [k]: v?.target ? v.target.value : v });
  return (
    <div className="p-5 grid grid-cols-2 md:grid-cols-12 gap-3 border-b border-slate-100" data-testid="disclosure-filters">
      <div className="relative col-span-2 md:col-span-4"><Search className="h-4 w-4 absolute left-3 top-3 text-slate-400" /><Input className="pl-9" placeholder="Search name or email" value={f.q} onChange={set("q")} data-testid="disclosure-search-input" /></div>
      <div className="md:col-span-2"><Select value={f.provider} onValueChange={set("provider")}><SelectTrigger data-testid="disclosure-provider-select"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All sign-ins</SelectItem><SelectItem value="email">Email</SelectItem><SelectItem value="google">Google</SelectItem></SelectContent></Select></div>
      <div className="md:col-span-2"><Select value={f.status} onValueChange={set("status")}><SelectTrigger data-testid="disclosure-status-select"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All statuses</SelectItem><SelectItem value="accepted">On current version</SelectItem><SelectItem value="pending">Awaiting confirmation</SelectItem></SelectContent></Select></div>
      <Input type="date" className="md:col-span-2" value={f.date_from} onChange={set("date_from")} title="Accepted from" data-testid="disclosure-date-from" />
      <Input type="date" className="md:col-span-2" value={f.date_to} onChange={set("date_to")} title="Accepted to" data-testid="disclosure-date-to" />
    </div>
  );
}

function Row({ u }) {
  return (
    <div className="px-5 py-3 flex items-center gap-4 text-sm" data-testid={`disclosure-record-${u.user_id}`}>
      <div className="flex-1 min-w-0"><div className="font-semibold text-slate-900 truncate">{u.name}</div><div className="text-xs text-slate-500 truncate">{u.email} · {(u.auth_provider || "email") === "google" ? "Google" : "Email"}</div></div>
      {u.disclosure_accepted_at && <div className="text-right text-xs"><div className="text-slate-900 font-medium">{new Date(u.disclosure_accepted_at).toLocaleString()}</div><div className="text-slate-500">{METHOD[u.disclosure_method] || "Recorded"} · v{u.disclosure_version}{u.disclosure_lang ? ` · ${u.disclosure_lang.toUpperCase()}` : ""}</div></div>}
      {!u.current && <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border bg-amber-50 text-amber-700 border-amber-200" data-testid={`disclosure-pending-${u.user_id}`}>Awaiting</span>}
    </div>
  );
}

export function DisclosureRecords() {
  const [d, setD] = useState(null);
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/admin/disclosure-records", { params: clean(f) }).then((r) => setD(r.data)).catch((e) => toast.error(formatErr(e))), [f]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const exportPdf = async () => {
    setBusy(true);
    const qs = new URLSearchParams(clean(f)).toString();
    try { await downloadFile(`/admin/disclosure-records/pdf${qs ? `?${qs}` : ""}`, `GrantVerify_disclosure_records_${new Date().toISOString().slice(0, 10)}.pdf`); }
    catch { toast.error("Couldn't export records"); }
    finally { setBusy(false); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-lg" data-testid="disclosure-records">
      <div className="p-5 flex flex-wrap items-center justify-between gap-3 border-b border-slate-100">
        <div className="flex gap-6 text-sm">
          <span><b className="font-display text-2xl text-slate-900" data-testid="disclosure-accepted-count">{d?.accepted ?? "–"}</b> <span className="text-slate-500">on current version</span></span>
          <span><b className="font-display text-2xl text-amber-600" data-testid="disclosure-pending-count">{d?.pending ?? "–"}</b> <span className="text-slate-500">awaiting next login</span></span>
        </div>
        <div className="flex gap-2">
          {JSON.stringify(f) !== JSON.stringify(EMPTY) && <Button variant="ghost" onClick={() => setF(EMPTY)} data-testid="disclosure-clear-filters">Clear filters</Button>}
          <Button onClick={exportPdf} disabled={busy} variant="outline" data-testid="disclosure-export-pdf-button">{busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}Export PDF report</Button>
        </div>
      </div>
      <Filters f={f} setF={setF} />
      <div className="max-h-[420px] overflow-y-auto divide-y divide-slate-100" data-testid="disclosure-records-list">
        {!d ? <Loader2 className="h-5 w-5 m-5 animate-spin text-emerald-600" /> : d.records.length === 0 ? <p className="p-6 text-sm text-slate-500" data-testid="disclosure-records-empty">No customers match.</p> : d.records.map((u) => <Row key={u.user_id} u={u} />)}
      </div>
    </div>
  );
}

export function LegalVersions() {
  const [d, setD] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/legal-versions").then((r) => setD(r.data)).catch((e) => toast.error(formatErr(e)));
  useEffect(() => { load(); }, []);
  const publish = async () => {
    if (!window.confirm("Publish a new version? Every customer will be asked to reconfirm at their next login.")) return;
    setBusy(true);
    try { const { data } = await api.post("/admin/legal-versions", { note }); toast.success(`Version ${data.version} published`); setNote(""); load(); }
    catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };
  if (!d) return <Loader2 className="h-5 w-5 animate-spin text-emerald-600" />;
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 grid md:grid-cols-12 gap-6 mb-4" data-testid="legal-versions">
      <div className="md:col-span-5">
        <div className="font-mono text-xs uppercase tracking-[0.18em] text-emerald-700">Current version</div>
        <div className="font-display text-4xl font-extrabold text-slate-900 mt-1" data-testid="legal-current-version">v{d.current}</div>
        <p className="text-xs text-slate-500 mt-1" data-testid="legal-version-stats">{d.on_current} confirmed · {d.awaiting} awaiting</p>
        <Textarea rows={2} className="mt-4" placeholder="What changed? e.g. Updated refund terms" value={note} onChange={(e) => setNote(e.target.value)} data-testid="legal-version-note-input" />
        <Button onClick={publish} disabled={busy || note.trim().length < 3} className="mt-2 bg-slate-900 hover:bg-slate-800" data-testid="legal-publish-button">{busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}Publish new version</Button>
      </div>
      <div className="md:col-span-7 max-h-56 overflow-y-auto divide-y divide-slate-100" data-testid="legal-versions-list">
        {d.versions.map((v) => (
          <div key={v.version} className="py-2.5 text-sm flex gap-3" data-testid={`legal-version-${v.version}`}>
            <span className="font-mono text-xs text-emerald-700 w-8 shrink-0 mt-0.5">v{v.version}</span>
            <div className="min-w-0"><div className="text-slate-900">{v.note}</div><div className="text-xs text-slate-500">{v.published_at ? `${new Date(v.published_at).toLocaleDateString()} · ${v.published_by}` : "Original launch version"}</div></div>
          </div>
        ))}
      </div>
    </div>
  );
}
